import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion, useScroll, useTransform} from 'framer-motion';
import {useTranslation} from 'react-i18next';

const TitleSection = () => {
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [hovered, setHovered] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [contentLoaded, setContentLoaded] = useState(false);
    const {t, i18n} = useTranslation('common');

    // Handle component mount and content load
    useEffect(() => {
        // Set a slight delay to allow DOM to settle before animations
        const timer = setTimeout(() => {
            setContentLoaded(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    // Handle video loading and playback
    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            // Set playback speed when video is fully loaded
            const handleCanPlay = () => {
                videoElement.playbackRate = 1.5;
                setVideoLoaded(true);

                videoElement.play().catch(error => {
                    console.log('Autoplay attempt failed:', error);
                    // Try again after a short delay
                    setTimeout(() => {
                        videoElement.play()
                            .then(() => videoElement.playbackRate = 1.5)
                            .catch(err => console.error('Retry play attempt failed:', err));
                    }, 1000);
                });
            };

            // Listen for canplaythrough which indicates the video can be played without buffering
            videoElement.addEventListener('canplaythrough', handleCanPlay);

            return () => {
                videoElement.removeEventListener('canplaythrough', handleCanPlay);
            };
        }
    }, []);

    // Subtle parallax effect on scroll
    const {scrollYProgress} = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"]
    });

    const titleY = useTransform(scrollYProgress, [0, 1], [0, -20]);
    const subtitleY = useTransform(scrollYProgress, [0, 1], [0, -10]);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);

    return (
        <section
            ref={sectionRef}
            className="w-full flex flex-col justify-center py-16 md:py-24 relative"
        >
            <div className="w-full px-4 md:px-6 lg:px-8 2xl:container 2xl:mx-auto">
                <div className="relative">

                    <div
                        className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
                        <motion.div
                            initial={{opacity: 0, y: 30}}
                            animate={{
                                opacity: contentLoaded ? 1 : 0,
                                y: contentLoaded ? 0 : 30
                            }}
                            transition={{
                                duration: 0.8,
                                ease: "easeOut"
                            }}
                            style={{opacity}}
                            className="lg:w-1/2 flex justify-center mb-10 lg:mb-0"
                        >
                            <div className="w-full flex flex-col">
                                <h1
                                    ref={titleRef}
                                    className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-4 leading-tight text-center md:text-left"
                                >
                                    {t('homepage.title')}
                                    <br/>
                                    <span
                                        className="bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent">
                                        {t('homepage.titleHighlight')}
                                    </span>
                                </h1>
                                <h2 className="text-xl sm:text-2xl font-medium text-gray-700 dark:text-gray-300 mb-1 text-center md:text-left block">
                                    Europe's only platform for customizable regulatory solutions
                                </h2>

                                <h3 className=" font-medium text-gray-700 dark:text-gray-300 mb-6 text-center md:text-left block">
                                    Select from 22+ different modules to create a custom regulatory solution for your
                                    business.
                                </h3>

                            </div>
                        </motion.div>

                        <motion.div
                            initial={{opacity: 0, y: 40}}
                            animate={{
                                opacity: videoLoaded && contentLoaded ? 1 : 0,
                                y: videoLoaded && contentLoaded ? 0 : 40
                            }}
                            transition={{
                                duration: 1.0,
                                delay: 0.3,
                                ease: "easeOut"
                            }}
                            className="lg:w-1/2 w-full flex justify-center items-center"
                        >
                            <div className="rounded-lg">
                                <video
                                    ref={videoRef}
                                    className="w-full h-full object-cover
                                    max-sm:w-[315px] max-sm:h-[315px]
                                    sm:w-[360px] sm:h-[360px]
                                    md:w-[405px] md:h-[405px]
                                    lg:w-[450px] lg:h-[450px]
                                    xl:w-[500px] xl:h-[500px]"
                                    muted
                                    loop
                                    playsInline
                                    preload="auto"
                                    style={{
                                        opacity: videoLoaded ? 1 : 0,
                                        transition: "opacity 1s ease-in-out"
                                    }}
                                >
                                    <source
                                        src="https://d1dpnrg5o25h3x.cloudfront.net/static/website/theme/videos/video-csrd-data.mp4"
                                        type="video/mp4"/>
                                    {/* Only include MOV source for iOS/macOS devices */}
                                    {/iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent) && (
                                        <source
                                            src="https://d1dpnrg5o25h3x.cloudfront.net/static/website/theme/videos/video-csrd-data.mov"
                                            type="video/quicktime"/>
                                    )}
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        </motion.div>
                    </div>


                </div>
            </div>
        </section>
    );
};

export default TitleSection;